import { useMemo } from 'react';
import { createAllModelsStatsMatrix } from '../helpers/ProjectPreviewHelper';
import { PieChartData } from '../../../../shared/models/common/PieChartData';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import { ChatType, projectChatType } from '../../../../shared/types/monitor/ChatType';
import { expandSelectedChatTypes } from '../../../../shared/helpers/MonitorTypeHelpers';
import {
  useEffectiveAddons,
  useEffectiveFeatures,
} from '../../../../shared/contexts/effective-features/EffectiveFeaturesContext';

export const useAllModelsStatsMatrix = (
  isProjectDailyStatsLoading: boolean,
  isMonitorPromptsLoading: boolean,
  isEventsLoading: boolean,
  firmName: string,
  accountResponse: AccountResponse | null,
  shareOfVoiceData: PieChartData[],
  keywordsMap: Map<string, string>,
  // Per-(model, brand) SoV from the backend — drives the matrix values AND the brand selection.
  perModelSov: Map<string, Map<string, number>>,
  hasExplicitModelFilter?: boolean,
  selectedChatTypes?: ChatType[]
) => {
  // Owner's models/addons when a superadmin views a client's project; own config elsewhere.
  const effectiveFeatures = useEffectiveFeatures();
  const effectiveAddons = useEffectiveAddons();

  const allowedChatTypes = useMemo(() => {
    if (!hasExplicitModelFilter) return null;
    if (!selectedChatTypes || selectedChatTypes.length === 0) return new Set<string>();
    return expandSelectedChatTypes(selectedChatTypes);
  }, [hasExplicitModelFilter, selectedChatTypes]);

  return useMemo(() => {
    if (
      isProjectDailyStatsLoading ||
      isMonitorPromptsLoading ||
      isEventsLoading ||
      !accountResponse ||
      !keywordsMap ||
      keywordsMap.size === 0
    ) {
      return {
        matrix: null,
        selectedKeywords: [],
      };
    }

    // Gate models/chat-types by the effective (client) config, not the logged-in superadmin's.
    const effectiveAccount: AccountResponse = {
      ...accountResponse,
      features: effectiveFeatures as AccountResponse['features'],
      addons: effectiveAddons as AccountResponse['addons'],
    };

    const visibleChatTypes = allowedChatTypes
      ? projectChatType(effectiveAccount, true).filter((ct) => allowedChatTypes.has(ct))
      : undefined;

    return createAllModelsStatsMatrix(
      firmName,
      effectiveAccount,
      shareOfVoiceData,
      perModelSov,
      visibleChatTypes
    );
  }, [
    isProjectDailyStatsLoading,
    isMonitorPromptsLoading,
    isEventsLoading,
    firmName,
    accountResponse,
    effectiveFeatures,
    effectiveAddons,
    shareOfVoiceData,
    keywordsMap,
    allowedChatTypes,
    perModelSov,
  ]);
};
